/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The renderer behind every terms component: a `TermsText` / a key with typed
 * variables (src/lib/termsPresentation.ts, src/lib/termsVerdict.ts) → the words
 * a visitor reads, in the active language.
 *
 * The pure modules decide WHAT is said and hand over full i18n keys with RAW
 * variables (a yearless day, an amount, a count, a payout key); this is the one
 * place those become text, so a number is formatted the same way in the row's
 * cell, the fact table and the bullets — and the baked HTML and the hydrating
 * client agree (src/lib/formatters.ts: site-locale months, no Intl for dates,
 * one number format per locale; never `.toFixed()` or a bare `toLocaleString()`).
 *
 * Exported separately so the packages that print the same values elsewhere (the
 * row's Uitbetaling cell, the compare table) format them through the same code.
 */

import { useLanguage, type Language } from '../../contexts/LanguageContext';
import { fillTemplate } from '../../lib/templateVars.js';
import {
  eurDecimals,
  formatCurrencyEUR,
  formatDateShort,
  formatInteger,
  formatMonthDay,
} from '../../lib/formatters';
import {
  describeOfferPricing,
  formatCommissionPct,
  formatKwhRate,
} from '../../lib/offerPricingText';
import type { TermsText, TermsVar } from '../../lib/termsPresentation';
import type { FactTone } from '../../lib/termsTone';
import { TONE_WORD_KEY } from './toneText';

/** `t` as the components receive it: a key path in, the active language's text out. */
export type Translate = (key: string) => string;

const stripFullStop = (s: string): string => s.trim().replace(/[.\s]+$/, '');

/**
 * A requirement written in words, as an item of a list: lower-cased after the
 * first item — unless its first word carries capitals of its own ("EAN-code",
 * "KvK-nummer"), which are spelling, not sentence case.
 */
function asListItem(text: string): string {
  const firstWord = text.split(/\s/, 1)[0] ?? '';
  return /[A-Z]/.test(firstWord.slice(1)) ? text : text.charAt(0).toLowerCase() + text.slice(1);
}

/**
 * The sign-up requirements as one value: "EAN-code van de aansluiting,
 * serienummer van de laadpaal". The codes are the dossier's own labels
 * (`dossier.value.signupRequirements.*`); `other` is the editor's note.
 */
function formatItems(t: Translate, codes: readonly string[], note: string): string {
  const words = codes
    .map((code) =>
      stripFullStop(code === 'other' ? note : t(`dossier.value.signupRequirements.${code}`))
    )
    .filter((word) => word !== '');
  const joined = words.map((word, i) => (i === 0 ? word : asListItem(word))).join(', ');
  return joined.charAt(0).toUpperCase() + joined.slice(1);
}

/** One raw variable → the text that fills its `{placeholder}`. */
export function formatTermsVar(t: Translate, language: Language, v: TermsVar): string {
  switch (v.kind) {
    case 'date':
      return formatMonthDay(v.monthDay, language, v.style);
    case 'iso_date':
      return formatDateShort(v.value, language);
    case 'eur':
      return formatCurrencyEUR(v.value, language, eurDecimals(v.value));
    case 'eur_rate':
      return formatCurrencyEUR(v.value, language, 2);
    case 'kwh':
    case 'count':
      return formatInteger(v.value, language);
    case 'months':
      return fillTemplate(
        t(v.value === 1 ? 'offer_terms.unit.months_one' : 'offer_terms.unit.months'),
        { count: formatInteger(v.value, language) }
      );
    case 'payout':
      return t(`table.payout_values.${v.key}`);
    case 'items':
      return formatItems(t, v.codes, v.note);
    case 'text':
      return v.value;
    case 'offer_pricing':
      return describeOfferPricing(t, language, v.pricing) ?? '';
    case 'pct':
      return formatCommissionPct(v.value, language);
    case 'eur_kwh':
      return formatKwhRate(v.value, language);
    case 'ladder':
      // Text only (a compare cell's words, the "Gelijk voor alle" line): the
      // inline description. A surface that can draw rows uses termsLadderParts.
      return describeOfferPricing(t, language, { model: 'tiered', tiers: v.tiers }) ?? '';
  }
}

type LadderVar = Extract<TermsVar, { kind: 'ladder' }>;

/**
 * A key whose variables hold a staffel's ladder, split for a surface that
 * draws the ladder as rows (DerivedBullets → StaffelLadder): the words before
 * its placeholder, filled, without the separator that led into the inline
 * form ("Verdienmodel: staffel — {model}" → "Verdienmodel: staffel"), the
 * ladder itself, and any words after it. Null when no variable is a ladder —
 * the caller prints `termsKeyText` as ever.
 */
export function termsLadderParts(
  t: Translate,
  language: Language,
  key: string,
  vars?: Record<string, TermsVar>
): { lead: string; ladder: LadderVar; rest: string } | null {
  const entry = Object.entries(vars ?? {}).find(([, v]) => v.kind === 'ladder');
  if (!entry) return null;
  const [name, ladder] = entry as [string, LadderVar];
  const template = t(key);
  const placeholder = `{${name}}`;
  const at = template.indexOf(placeholder);
  if (at < 0) return null;
  const filled: Record<string, string> = {};
  for (const [other, value] of Object.entries(vars ?? {})) {
    if (other !== name) filled[other] = formatTermsVar(t, language, value);
  }
  return {
    lead: fillTemplate(template.slice(0, at), filled).replace(/[\s:\u2013\u2014-]+$/u, ''),
    ladder,
    rest: fillTemplate(template.slice(at + placeholder.length), filled).trim(),
  };
}

/** A full key with its variables, filled. */
export function termsKeyText(
  t: Translate,
  language: Language,
  key: string,
  vars?: Record<string, TermsVar>
): string {
  const filled: Record<string, string> = {};
  for (const [name, value] of Object.entries(vars ?? {})) {
    filled[name] = formatTermsVar(t, language, value);
  }
  return fillTemplate(t(key), filled);
}

/** A `TermsText`: a key with variables, or the text a person wrote. */
export function termsText(t: Translate, language: Language, text: TermsText): string {
  return 'text' in text ? text.text : termsKeyText(t, language, text.key, text.vars);
}

/** The sr-only judgement of a tone ("gunstig" / "let op"); '' where there is none. */
export function toneWord(t: Translate, tone: FactTone): string {
  const key = TONE_WORD_KEY[tone];
  return key ? t(key) : '';
}

/** The renderer bound to the active language — what the components call. */
export function useTermsText() {
  const { t, language } = useLanguage();
  return {
    t: t as Translate,
    language,
    /** A `TermsText` → its words. */
    text: (text: TermsText): string => termsText(t, language, text),
    /** A key with variables → its words. */
    keyText: (key: string, vars?: Record<string, TermsVar>): string =>
      termsKeyText(t, language, key, vars),
    /** A tone → its sr-only word, '' for neutral and muted. */
    toneWord: (tone: FactTone): string => toneWord(t, tone),
  };
}
